import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { SettingsCard } from '../components/SettingsCard';
import { EmptyState, ErrorNotice, Field, Spinner, formatDate } from '../components/ui';
import type { DonationFund, MembershipCategory, MembershipYearSummary } from '../types';

/**
 * The settings screen: which years exist, what a membership costs in each,
 * and the categories and donation funds those years price. Years get their
 * own detail page (RatesYearPage) because a year's rates and BDI bands are
 * where the real complexity — and the real risk — lives; this page is the
 * index onto that, plus the two smaller tables that aren't year-scoped.
 */
export function RatesPage() {
  const [years, setYears] = useState<MembershipYearSummary[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [showNewYear, setShowNewYear] = useState(false);

  function load() {
    api
      .get<{ years: MembershipYearSummary[] }>('/membership-years')
      .then((result) => setYears(result.years))
      .catch(setError);
  }

  useEffect(load, []);

  return (
    <>
      <div className="page-head">
        <h1>Rates &amp; settings</h1>
      </div>

      <ErrorNotice error={error} />

      <div className="card">
        <div className="page-head card-head">
          <h2>Membership years</h2>
          {!showNewYear ? (
            <button type="button" className="primary" onClick={() => setShowNewYear(true)}>
              New year
            </button>
          ) : null}
        </div>

        <p className="muted small">
          Rates and BDI bands can only be added to or removed from a year before its
          renewal window opens. Once a year opens, its numbers are locked for good —
          that's what keeps last year's figures from moving when this year's change.
        </p>

        {showNewYear ? (
          <NewYearForm
            onCancel={() => setShowNewYear(false)}
            onCreated={() => {
              setShowNewYear(false);
              load();
            }}
          />
        ) : null}

        {years === null && error === null ? <Spinner label="Loading years" /> : null}

        {years !== null && years.length === 0 ? (
          <EmptyState title="No membership years yet">
            Create one to start setting rates.
          </EmptyState>
        ) : null}

        {years !== null && years.length > 0 ? (
          <table className="members">
            <thead>
              <tr>
                <th scope="col">Year</th>
                <th scope="col">Opens</th>
                <th scope="col">Renewal due</th>
                <th scope="col">BDI cutoff</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {years.map((year) => (
                <tr key={year.id}>
                  <td data-label="Year">
                    <Link to={`/rates/${year.id}`} className="member-name">
                      {year.year}
                    </Link>
                  </td>
                  <td data-label="Opens">{formatDate(year.opens_on)}</td>
                  <td data-label="Renewal due">{formatDate(year.due_on)}</td>
                  <td data-label="BDI cutoff">{formatDate(year.bdi_cutoff_date)}</td>
                  <td data-label="Status">
                    {year.is_current ? <span className="pill pill-active">current</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>

      <CategoriesCard />
      <DonationFundsCard />
      <SettingsCard />
    </>
  );
}

function NewYearForm({ onCancel, onCreated }: { onCancel: () => void; onCreated: () => void }) {
  const [year, setYear] = useState(String(new Date().getFullYear() + 1));
  const [opensOn, setOpensOn] = useState('');
  const [dueOn, setDueOn] = useState('');
  const [bdiCutoff, setBdiCutoff] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post('/membership-years', {
        year: Number(year),
        opens_on: opensOn,
        due_on: dueOn,
        bdi_cutoff_date: bdiCutoff,
      });
      onCreated();
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card form wide inline-form" onSubmit={onSubmit}>
      <ErrorNotice error={error} />
      <div className="grid">
        <Field
          label="Year"
          type="number"
          required
          value={year}
          onChange={(e) => setYear(e.target.value)}
        />
        <Field
          label="Opens on"
          type="date"
          required
          value={opensOn}
          onChange={(e) => setOpensOn(e.target.value)}
        />
        <Field
          label="Renewal due"
          type="date"
          required
          value={dueOn}
          onChange={(e) => setDueOn(e.target.value)}
        />
        <Field
          label="BDI cutoff"
          type="date"
          required
          hint="31 March in most years. After this, cover is delayed for late payers."
          value={bdiCutoff}
          onChange={(e) => setBdiCutoff(e.target.value)}
        />
      </div>
      <div className="actions">
        <button type="submit" className="primary" disabled={busy}>
          {busy ? 'Creating…' : 'Create year'}
        </button>
        <button type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function CategoriesCard() {
  const [categories, setCategories] = useState<MembershipCategory[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [showNew, setShowNew] = useState(false);

  function load() {
    api
      .get<{ categories: MembershipCategory[] }>('/membership-categories')
      .then((result) => setCategories(result.categories))
      .catch(setError);
  }

  useEffect(load, []);

  async function toggleActive(category: MembershipCategory) {
    setError(null);
    try {
      await api.put(`/membership-categories/${category.id}`, { is_active: !category.is_active });
      load();
    } catch (failure) {
      setError(failure);
    }
  }

  return (
    <div className="card">
      <div className="page-head card-head">
        <h2>Membership categories</h2>
        {!showNew ? (
          <button type="button" onClick={() => setShowNew(true)}>
            New category
          </button>
        ) : null}
      </div>

      <ErrorNotice error={error} />

      {showNew ? (
        <NewCategoryForm
          onCancel={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            load();
          }}
        />
      ) : null}

      {categories === null ? (
        <Spinner label="Loading categories" />
      ) : (
        <table className="members">
          <thead>
            <tr>
              <th scope="col">Code</th>
              <th scope="col">Name</th>
              <th scope="col">Rules</th>
              <th scope="col">Active</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((category) => (
              <tr key={category.id}>
                <td data-label="Code">
                  <code>{category.code}</code>
                </td>
                <td data-label="Name">{category.name}</td>
                <td data-label="Rules">
                  {[
                    category.requires_parent ? 'requires a full member' : null,
                    category.max_age !== null ? `under ${category.max_age}` : null,
                    category.carries_bdi ? 'carries BDI' : 'no BDI',
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </td>
                <td data-label="Active">
                  <label className="checkbox small-checkbox">
                    <input
                      type="checkbox"
                      checked={category.is_active}
                      onChange={() => void toggleActive(category)}
                    />
                    {category.is_active ? 'Active' : 'Retired'}
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function NewCategoryForm({ onCancel, onCreated }: { onCancel: () => void; onCreated: () => void }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post('/membership-categories', { code, name });
      onCreated();
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card form wide inline-form" onSubmit={onSubmit}>
      <ErrorNotice error={error} />
      <div className="grid">
        <Field
          label="Code"
          required
          hint="Lowercase, no spaces — this cannot be changed later."
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
        <Field label="Name" required value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="actions">
        <button type="submit" className="primary" disabled={busy}>
          {busy ? 'Creating…' : 'Create category'}
        </button>
        <button type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function DonationFundsCard() {
  const [funds, setFunds] = useState<DonationFund[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  function load() {
    api
      .get<{ funds: DonationFund[] }>('/donation-funds')
      .then((result) => setFunds(result.funds))
      .catch(setError);
  }

  useEffect(load, []);

  async function toggleActive(fund: DonationFund) {
    setError(null);
    try {
      await api.put(`/donation-funds/${fund.id}`, { is_active: !fund.is_active });
      load();
    } catch (failure) {
      setError(failure);
    }
  }

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post('/donation-funds', { name: newName });
      setNewName('');
      setShowNew(false);
      load();
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="page-head card-head">
        <h2>Donation funds</h2>
        {!showNew ? (
          <button type="button" onClick={() => setShowNew(true)}>
            New fund
          </button>
        ) : null}
      </div>

      <ErrorNotice error={error} />

      {showNew ? (
        <form className="form wide inline-form" onSubmit={onCreate}>
          <div className="grid">
            <Field label="Fund name" required autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} />
          </div>
          <div className="actions">
            <button type="submit" className="primary" disabled={busy}>
              {busy ? 'Creating…' : 'Create fund'}
            </button>
            <button type="button" onClick={() => setShowNew(false)} disabled={busy}>
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {funds === null ? (
        <Spinner label="Loading funds" />
      ) : (
        <ul className="permission-list">
          {funds.map((fund) => (
            <li key={fund.id}>
              <label className="checkbox small-checkbox">
                <input
                  type="checkbox"
                  checked={fund.is_active}
                  onChange={() => void toggleActive(fund)}
                />
                {fund.name}
                {!fund.is_active ? <span className="muted"> (retired — not offered to new donors)</span> : null}
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
